import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import axios from 'axios';
import 'mapbox-gl/dist/mapbox-gl.css';
import { supabase } from './supabase';
import Auth from './Auth';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN!;
const API = process.env.REACT_APP_API_URL;

interface ZoneRules {
  zone_code: string;
  zone_label: string;
  zone_category: string;
  zone_subtype: string;
  zone_guide: string;
  zone_guide_link: string;
  jurisdiction: string;
  state: string;
  setbacks_ft: Record<string, {value:number|string,unit:string,note:string}>;
  setback_notes: Record<string, string>;
  hard_limits: Record<string, {value:number,unit:string,note:string}>;
  soft_constraints: Record<string, {value:string,unit:string,note:string}>;
  parking: Record<string, string>;
  permitted_uses: Array<{use_label:string,use_code:string,permission_type:string}>;
  rules_source: string;
  last_updated: string;
  prohibited_uses: string[];
}

interface ParcelData {
  apn: string;
  address: string;
  city: string;
  state: string;
  owner: string;
  land_value: number;
  improvement_value: number;
  year_built: number;
  use_description: string;
  zoning_code: string;
  zoning_description: string;
  zoning_type: string;
  max_height_ft: number;
  max_far: number;
  area_sqft: number;
  area_acres: number;
  fema_risk: string;
  median_household_income: number;
  centroid: {lat: number; lon: number};
  polygon_geojson: any;
  zone_rules?: ZoneRules;
}

export default function App() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [parcel, setParcel] = useState<ParcelData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [buildable, setBuildable] = useState<any>(null);
  const [layout, setLayout] = useState<any>(null);
  const [layoutLoading, setLayoutLoading] = useState(false);
  const [layoutGoal, setLayoutGoal] = useState('maximize_units');
  const [session, setSession] = useState<any>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const isMobile = () => window.innerWidth < 768;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session?.user?.id) {
        axios.get(`${API}/billing/subscription/${session.user.id}`)
          .then(res => setSubscription(res.data))
          .catch(() => setSubscription({ plan_tier: 'free' }));
      }
    });
    supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user?.id) {
        axios.get(`${API}/billing/subscription/${session.user.id}`)
          .then(res => setSubscription(res.data))
          .catch(() => setSubscription({ plan_tier: 'free' }));
      }
    });
  }, []);

  useEffect(() => {
    if (!session || map.current) return;
    map.current = new mapboxgl.Map({
      container: mapContainer.current!,
      style: 'mapbox://styles/mapbox/light-v11',
      center: [-96.7970, 32.7767],
      zoom: 13,
    });

    map.current.on('load', () => {
      map.current!.addSource('parcel', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
      map.current!.addLayer({
        id: 'parcel-fill', type: 'fill', source: 'parcel',
        paint: { 'fill-color': '#0D9488', 'fill-opacity': 0.18 }
      });
      map.current!.addLayer({
        id: 'parcel-outline', type: 'line', source: 'parcel',
        paint: { 'line-color': '#0D9488', 'line-width': 2.5 }
      });
      map.current!.addSource('buildable', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] }
      });
      map.current!.addLayer({
        id: 'buildable-fill', type: 'fill', source: 'buildable',
        paint: { 'fill-color': '#F59E0B', 'fill-opacity': 0.25 }
      });
      map.current!.addLayer({
        id: 'buildable-outline', type: 'line', source: 'buildable',
        paint: {
          'line-color': '#F59E0B',
          'line-width': 1.5,
          'line-dasharray': [3, 2]
        }
      });
    });

    map.current.on('click', handleMapClick);
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  const handleMapClick = async (e: mapboxgl.MapMouseEvent) => {
    const { lat, lng } = e.lngLat;
    fetchParcel(null, lat, lng);
  };

  const handleAddressSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    fetchParcel(address.trim(), null, null);
  };

  const fetchParcel = async (
    addr: string | null,
    lat: number | null,
    lon: number | null
  ) => {
    setLoading(true); setError(null); setParcel(null);
    setBuildable(null); setLayout(null);
    if (isMobile()) setSheetOpen(false);
    try {
      let res;
      if (addr) {
        res = await axios.get(`${API}/parcels/lookup/address`, {
          params: { address: addr, user_id: session?.user?.id }
        });
      } else {
        res = await axios.get(`${API}/parcels/lookup/coords`, {
          params: { lat, lon, user_id: session?.user?.id }
        });
      }

      const data: ParcelData = res.data;
      setParcel(data);

      (map.current!.getSource('parcel') as mapboxgl.GeoJSONSource)
        .setData({
          type: 'Feature',
          geometry: data.polygon_geojson,
          properties: {}
        });

      const { lat: pLat, lon: pLon } = data.centroid;
      if (marker.current) marker.current.remove();
      marker.current = new mapboxgl.Marker({ color: '#0D9488' })
        .setLngLat([pLon, pLat])
        .addTo(map.current!);
      map.current!.flyTo({ center: [pLon, pLat], zoom: 17, duration: 1000 });

      try {
       const bRes = await axios.post(
          `${API}/parcels/buildable-envelope?user_id=${session?.user?.id}`,
          {
            polygon_geojson: data.polygon_geojson,
            lat: data.centroid.lat,
            lon: data.centroid.lon,
          }
        );
        setBuildable(bRes.data);
        if (bRes.data.buildable_geojson) {
          (map.current!.getSource('buildable') as mapboxgl.GeoJSONSource)
            .setData({
              type: 'Feature',
              geometry: bRes.data.buildable_geojson,
              properties: {}
            });
        }
      } catch (e) {
        setBuildable(null);
      }

      // Auto-open sheet on mobile when parcel loads
      if (isMobile()) setSheetOpen(true);

    } catch (err: any) {
      setError(err.response?.data?.detail || 'Could not load parcel. Try a Dallas, TX address.');
      if (isMobile()) setSheetOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const saveParcel = async () => {
    if (!parcel || !session) return;
    const { error } = await supabase.from('saved_parcels').insert({
      user_id: session.user.id,
      apn: parcel.apn,
      address: parcel.address,
      city: parcel.city,
      state: parcel.state,
      parcel_data: parcel,
    });
    setSavedMsg(error ? 'Error saving' : 'Parcel saved!');
    setTimeout(() => setSavedMsg(null), 3000);
  };

  const generateLayout = async () => {
    if (!parcel) return;
    setLayoutLoading(true); setLayout(null);
    try {
      const res = await axios.post(
        `${API}/parcels/generate-layout?user_id=${session?.user?.id}`,
        {
          parcel,
          lat: parcel.centroid.lat,
          lon: parcel.centroid.lon,
          goal: layoutGoal,
        }
      );
      setLayout(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLayoutLoading(false);
    }
  };

  const exportPdf = async () => {
    if (!parcel) return;
    try {
      const res = await axios.post(`${API}/parcels/export-pdf?user_id=${session?.user?.id}`, {
        parcel,
        buildable: buildable || {},
        layout: layout || null,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${parcel.apn || 'landframe-report'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF export failed', e);
    }
  };

 const handleUpgrade = async (plan: 'starter' | 'pro') => {
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        user_id: session.user.id,
        email: session.user.email,
        plan,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      console.error('Checkout failed', e);
    }
  };

  if (!session) return <Auth />;

  const sidebarContent = (
    <div style={{ padding:'14px 16px', flex: 1, overflowY:'auto' }}>
      {loading && (
        <div style={{color:'#0D9488',fontSize:13,padding:'8px 0'}}>
          Loading parcel...
        </div>
      )}

      {error && (
  <div style={{fontSize:13,color:'#c0392b',background:'#fdf0ef',
    padding:'10px',borderRadius:6,marginBottom:10}}>
    {error}
    {error.includes('upgrade') && (
      <div style={{marginTop:10}}>
        <button onClick={() => handleUpgrade('starter')} style={{
          width:'100%', padding:'8px', fontSize:12, fontWeight:500,
          background:'#1a1a18', color:'#fff', border:'none',
          borderRadius:6, cursor:'pointer', marginBottom:6
        }}>Starter — $299/mo</button>
        <button onClick={() => handleUpgrade('pro')} style={{
          width:'100%', padding:'8px', fontSize:12, fontWeight:500,
          background:'#0D9488', color:'#fff', border:'none',
          borderRadius:6, cursor:'pointer'
        }}>Pro — $799/mo</button>
      </div>
    )}
  </div>
)}

      {parcel && (
        <div>
          <Section label="Parcel" />
          <Row label="Address" value={parcel.address} />
          <Row label="City" value={`${parcel.city}, ${parcel.state}`} />
          <Row label="APN" value={parcel.apn} />
          <Row label="Owner" value={parcel.owner} />
          <Row label="Area" value={`${parcel.area_sqft?.toLocaleString()} sf (${parcel.area_acres} ac)`} />
          <Row label="Land value" value={parcel.land_value ? `$${parcel.land_value.toLocaleString()}` : '—'} />
          <Row label="Improvement" value={parcel.improvement_value ? `$${parcel.improvement_value.toLocaleString()}` : '—'} />
          <Row label="Use" value={parcel.use_description} />
          <Row label="FEMA risk" value={parcel.fema_risk} />

          {buildable && (
            <div>
              <Section label="Buildable envelope" />
              <div style={{background:'#FFFBEB',borderRadius:6,
                padding:'8px 10px',marginBottom:10}}>
                <div style={{fontSize:18,fontWeight:500,color:'#F59E0B'}}>
                  {buildable.buildable_sqft?.toLocaleString()} sf
                </div>
                <div style={{fontSize:12,color:'#5f5e5a'}}>
                  {buildable.buildable_pct}% of lot · {buildable.setback_used_ft}ft setback applied
                </div>
              </div>
            </div>
          )}

          <Section label="Zoning (REGRID)" />
          <div style={{
            background:'#E1F5EE', borderRadius:6,
            padding:'8px 10px', marginBottom:10
          }}>
            <div style={{fontSize:20,fontWeight:500,color:'#0D9488'}}>
              {parcel.zoning_code || '—'}
            </div>
            <div style={{fontSize:12,color:'#5f5e5a'}}>
              {parcel.zoning_description}
            </div>
          </div>
          <Row label="Type" value={parcel.zone_rules?.zone_category || parcel.zoning_type} />
<Row label="Subtype" value={parcel.zone_rules?.zone_subtype || '—'} />
<Row label="Max height" value={
  parcel.zone_rules?.hard_limits?.max_height_ft
    ? `${parcel.zone_rules.hard_limits.max_height_ft.value} ft`
    : parcel.max_height_ft ? `${parcel.max_height_ft} ft` : '—'
} />
<Row label="Max FAR" value={
  parcel.zone_rules?.hard_limits?.max_far
    ? parcel.zone_rules.hard_limits.max_far.value.toString()
    : parcel.max_far?.toString() || '—'
} />
<Row label="Min lot area" value={
  parcel.zone_rules?.hard_limits?.min_lot_area_sqft
    ? `${parcel.zone_rules.hard_limits.min_lot_area_sqft.value.toLocaleString()} sqft`
    : '—'
} />
<Row label="Min landscaping" value={
  parcel.zone_rules?.soft_constraints?.min_landscaping_pct
    ? `${parcel.zone_rules.soft_constraints.min_landscaping_pct.value}%`
    : '—'
} />

{parcel.zone_rules?.zone_guide && (
  <div style={{
    fontSize:11, color:'#5f5e5a', lineHeight:1.5,
    marginTop:8, marginBottom:8, padding:'8px 10px',
    background:'#f8f7f3', borderRadius:6
  }}>
    {parcel.zone_rules.zone_guide.substring(0, 200)}...
  </div>
)}

{parcel.zone_rules?.zone_guide_link && (
  <a href={parcel.zone_rules.zone_guide_link} target="_blank"
    rel="noreferrer" style={{
      fontSize:11, color:'#0D9488', textDecoration:'none',
      display:'block', marginBottom:8
    }}>
    View full zoning code →
  </a>
)}

          {parcel.zone_rules && Object.keys(parcel.zone_rules.setbacks_ft || {}).length > 0 && (
            <div>
              <Section label="Setbacks (Zoneomics)" />
              {Object.entries(parcel.zone_rules.setbacks_ft).map(([k,v]) => (
                <Row key={k} label={k.replace(/_/g,' ')} value={`${v.value} ${v.unit}`} />
              ))}
            </div>
          )}

          {parcel.zone_rules && Object.keys(parcel.zone_rules.hard_limits || {}).length > 0 && (
  <div>
    <Section label="Zoning rules (Zoneomics)" />
    {Object.entries(parcel.zone_rules.hard_limits).map(([k,v]) => {
      const labels: Record<string, string> = {
        max_height_ft: 'Max height',
        max_far: 'Max FAR',
        min_lot_area_sqft: 'Min lot area',
        min_lot_width_ft: 'Min lot width',
        max_lot_coverage: 'Max lot coverage',
      };
      const label = labels[k] || k.replace(/_/g,' ');
      const value = k === 'max_height_ft' ? `${v.value} ft`
        : k === 'max_far' ? v.value.toString()
        : k.includes('sqft') ? `${v.value.toLocaleString()} sqft`
        : k.includes('pct') || k.includes('coverage') ? `${v.value}%`
        : `${v.value} ${v.unit}`;
      return <Row key={k} label={label} value={value} />;
    })}
    {parcel.zone_rules.soft_constraints && Object.keys(parcel.zone_rules.soft_constraints).length > 0 && (
      Object.entries(parcel.zone_rules.soft_constraints).map(([k,v]) => {
        const labels: Record<string, string> = {
  min_landscaping_pct: 'Min landscaping',
  max_density_du_per_acre: 'Max density',
  adu_permitted: 'ADU permitted',
  str_permitted: 'Short term rental',
  single_family_permitted: 'Single family',
  multi_family_permitted: 'Multi family',
  commercial_permitted: 'Commercial uses',
};
        const label = labels[k] || k.replace(/_/g,' ');
        return <Row key={k} label={label} value={v.unit === 'boolean' ? v.value : `${v.value}${v.unit === 'pct' ? '%' : ' ' + v.unit}`} />;
      })
    )}
  </div>
)}

{parcel.zone_rules?.setback_notes && Object.keys(parcel.zone_rules.setback_notes).length > 0 && (
  <div>
    <Section label="Setback notes (Zoneomics)" />
    {Object.entries(parcel.zone_rules.setback_notes).map(([k, v]) => (
      <div key={k} style={{
        fontSize:11, color:'#5f5e5a', lineHeight:1.5,
        padding:'6px 0', borderBottom:'0.5px solid #f0eeea'
      }}>
        <span style={{fontWeight:500,textTransform:'capitalize',color:'#888'}}>
          {k} setback:
        </span>{' '}{v}
      </div>
    ))}
  </div>
)}

{parcel.zone_rules?.parking && Object.keys(parcel.zone_rules.parking).length > 0 && (
  <div>
    <Section label="Parking requirements (Zoneomics)" />
    {Object.entries(parcel.zone_rules.parking).map(([k, v]) => (
      <Row key={k} label={k.replace(/_/g,' ')} value={String(v)} />
    ))}
  </div>
)}

{parcel.zone_rules?.permitted_uses && parcel.zone_rules.permitted_uses.length > 0 && (
  <div>
    <Section label="Permitted uses (Zoneomics)" />
    {parcel.zone_rules.permitted_uses.slice(0, 10).map((use, i) => (
      <div key={i} style={{
        fontSize:11, padding:'4px 0',
        borderBottom:'0.5px solid #f0eeea',
        color:'#3d3d3a'
      }}>
        <span style={{
  display:'inline-block',
  background: use.permission_type === 'conditional' ? '#FEF3C7' : '#E1F5EE',
  color: use.permission_type === 'conditional' ? '#92400E' : '#0D9488',
  fontSize:10, padding:'1px 6px',
  borderRadius:4, marginRight:6
}}>
  {use.permission_type === 'conditional' ? 'Conditional' : 'As of right'}
</span>
        {use.use_label}
      </div>
    ))}
    {parcel.zone_rules.permitted_uses.length > 10 && (
      <div style={{fontSize:11,color:'#888',marginTop:4}}>
        +{parcel.zone_rules.permitted_uses.length - 10} more permitted uses
      </div>
    )}
  </div>
)}
{parcel.zone_rules?.prohibited_uses && parcel.zone_rules.prohibited_uses.length > 0 && (
  <div>
    <Section label="Prohibited uses (Zoneomics)" />
    {parcel.zone_rules.prohibited_uses.slice(0, 8).map((use, i) => (
      <div key={i} style={{
        fontSize:11, padding:'4px 0',
        borderBottom:'0.5px solid #f0eeea',
        color:'#c0392b'
      }}>
        ✕ {use}
      </div>
    ))}
    {parcel.zone_rules.prohibited_uses.length > 8 && (
      <div style={{fontSize:11,color:'#888',marginTop:4}}>
        +{parcel.zone_rules.prohibited_uses.length - 8} more prohibited uses
      </div>
    )}
  </div>
)}
          <Section label="Demographics" />
          <Row label="Median income" value={parcel.median_household_income ?
            `$${parcel.median_household_income.toLocaleString()}` : '—'} />

          <button onClick={saveParcel} style={{
            width:'100%', marginTop:12, padding:'9px', fontSize:13, fontWeight:500,
            background:'#1a1a18', color:'#fff', border:'none',
            borderRadius:6, cursor:'pointer'
          }}>Save parcel</button>
          {savedMsg && (
            <div style={{fontSize:12,color:'#27500a',marginTop:6,textAlign:'center'}}>
              {savedMsg}
            </div>
          )}

<Section label="Your plan" />
<div style={{background:'#f8f7f3',borderRadius:8,padding:'10px 12px',marginBottom:10}}>
  <div style={{fontSize:12,color:'#888',marginBottom:8}}>
    Upgrade to search more parcels
  </div>
  <button onClick={() => handleUpgrade('starter')} style={{
    width:'100%', padding:'8px', fontSize:12, fontWeight:500,
    background:'#1a1a18', color:'#fff', border:'none',
    borderRadius:6, cursor:'pointer', marginBottom:6
  }}>Starter — $299/mo · 10 parcels</button>
  <button onClick={() => handleUpgrade('pro')} style={{
    width:'100%', padding:'8px', fontSize:12, fontWeight:500,
    background:'#0D9488', color:'#fff', border:'none',
    borderRadius:6, cursor:'pointer'
  }}>Pro — $799/mo · 50 parcels</button>
</div>

          <Section label="AI layout planner" />
{subscription?.plan_tier !== 'pro' && subscription?.plan_tier !== 'enterprise' ? (
  <div style={{
    background:'#F5F3FF', borderRadius:8,
    padding:'12px', marginBottom:10, textAlign:'center'
  }}>
    <div style={{fontSize:13,fontWeight:500,color:'#5B21B6',marginBottom:6}}>
      Pro feature
    </div>
    <div style={{fontSize:12,color:'#888',marginBottom:10}}>
      AI layout generation requires a Pro plan
    </div>
    <button onClick={() => handleUpgrade('pro')} style={{
      width:'100%', padding:'8px', fontSize:12, fontWeight:500,
      background:'#7C3AED', color:'#fff', border:'none',
      borderRadius:6, cursor:'pointer'
    }}>Upgrade to Pro — $799/mo</button>
  </div>
) : (
  <>
    <select value={layoutGoal} onChange={e => setLayoutGoal(e.target.value)}
      style={{width:'100%',padding:'7px 10px',fontSize:12,
        border:'1px solid #d3d1c7',borderRadius:6,marginBottom:8}}>
      <option value="maximize_units">Maximize units</option>
      <option value="maximize_open_space">Maximize open space</option>
      <option value="mixed_use">Mixed use</option>
    </select>
    <button onClick={generateLayout} disabled={layoutLoading} style={{
      width:'100%', padding:'9px', fontSize:13, fontWeight:500,
      background:'#7C3AED', color:'#fff', border:'none',
      borderRadius:6, cursor:'pointer', marginBottom:10
    }}>{layoutLoading ? 'Generating...' : 'Generate layout'}</button>
  </>
)}
          {layout && !layout.parse_error && (
            <div style={{background:'#F5F3FF',borderRadius:8,padding:'12px',fontSize:12}}>
              <div style={{fontWeight:500,color:'#5B21B6',marginBottom:8}}>
                {layout.strategy}
              </div>
              <Row label="Est. units" value={layout.estimated_units || '—'} />
              <Row label="Est. GSF" value={layout.estimated_gsf?.toLocaleString()} />
              <Row label="Parking" value={layout.parking_config} />
              <Row label="Access" value={layout.access_point} />
              <Row label="Confidence" value={layout.confidence} />
              <div style={{marginTop:8,color:'#5f5e5a',lineHeight:1.5}}>
                {layout.feasibility_notes}
              </div>
            </div>
          )}

          <button onClick={exportPdf} style={{
            width:'100%', marginTop:8, padding:'9px', fontSize:13, fontWeight:500,
            background:'#185fa5', color:'#fff', border:'none',
            borderRadius:6, cursor:'pointer'
          }}>Export PDF</button>
        </div>
      )}

      {!parcel && !loading && !error && (
        <div style={{color:'#888',fontSize:13,paddingTop:8}}>
          Search a Dallas, TX address above or click any location on the map.
        </div>
      )}
    </div>
  );

  return (
    <>
      <style>{`
        @media (max-width: 767px) {
          .desktop-sidebar { display: none !important; }
          .mobile-sheet {
            position: fixed;
            bottom: 0; left: 0; right: 0;
            background: #fff;
            border-radius: 16px 16px 0 0;
            box-shadow: 0 -4px 24px rgba(0,0,0,0.12);
            z-index: 200;
            transition: transform 0.3s ease;
            max-height: 80vh;
            display: flex;
            flex-direction: column;
          }
          .mobile-sheet.closed { transform: translateY(calc(100% - 56px)); }
          .mobile-sheet.open { transform: translateY(0); }
          .mobile-fab {
            position: fixed;
            bottom: 72px; right: 16px;
            background: #0D9488;
            color: #fff;
            border: none;
            border-radius: 50%;
            width: 52px; height: 52px;
            font-size: 22px;
            box-shadow: 0 4px 16px rgba(13,148,136,0.35);
            cursor: pointer;
            z-index: 150;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .mobile-search-bar {
            position: fixed;
            top: 12px; left: 12px; right: 12px;
            z-index: 150;
            display: flex;
            gap: 8px;
          }
        }
        @media (min-width: 768px) {
          .mobile-sheet { display: none !important; }
          .mobile-fab { display: none !important; }
          .mobile-search-bar { display: none !important; }
        }
      `}</style>

      <div style={{ display:'flex', height:'100vh', fontFamily:'-apple-system, sans-serif', position:'relative' }}>

        <div ref={mapContainer} style={{ flex: 1 }} />

        {/* ── Desktop sidebar ── */}
        <div className="desktop-sidebar" style={{
          width: 340, background:'#fff', borderLeft:'1px solid #e5e5e5',
          overflowY:'auto', display:'flex', flexDirection:'column', flexShrink: 0
        }}>
          <div style={{ padding:'16px 16px 12px', borderBottom:'1px solid #f0eeea' }}>
            <div style={{ fontSize:15, fontWeight:500, marginBottom:2 }}>LandFrame</div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <span style={{fontSize:11,color:'#888'}}>{session.user.email}</span>
              <span style={{fontSize:11,color:'#0D9488',cursor:'pointer'}}
                onClick={() => supabase.auth.signOut()}>Sign out</span>
            </div>
            <div style={{ fontSize:11, color:'#888', marginBottom:12 }}>
              Dallas County, TX · Click map or search
            </div>
            <form onSubmit={handleAddressSearch} style={{ display:'flex', gap:6 }}>
              <input
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="1000 Main St Dallas TX"
                style={{
                  flex:1, padding:'7px 10px', fontSize:13,
                  border:'1px solid #d3d1c7', borderRadius:6, outline:'none'
                }}
              />
              <button type="submit" style={{
                padding:'7px 12px', fontSize:12, fontWeight:500,
                background:'#0D9488', color:'#fff', border:'none',
                borderRadius:6, cursor:'pointer'
              }}>Go</button>
            </form>
          </div>
          {sidebarContent}
        </div>

        {/* ── Mobile floating search bar ── */}
        <div className="mobile-search-bar">
          <form onSubmit={handleAddressSearch} style={{ display:'flex', gap:8, flex:1 }}>
            <input
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Search Dallas address..."
              style={{
                flex:1, padding:'10px 14px', fontSize:14,
                border:'none', borderRadius:10, outline:'none',
                boxShadow:'0 2px 12px rgba(0,0,0,0.15)',
                background:'#fff',
              }}
            />
            <button type="submit" style={{
              padding:'10px 16px', fontSize:13, fontWeight:500,
              background:'#0D9488', color:'#fff', border:'none',
              borderRadius:10, cursor:'pointer',
              boxShadow:'0 2px 12px rgba(13,148,136,0.3)',
            }}>Go</button>
          </form>
        </div>

        {/* ── Mobile bottom sheet ── */}
        <div className={`mobile-sheet ${sheetOpen ? 'open' : 'closed'}`}>
          {/* Drag handle + header */}
          <div
            onClick={() => setSheetOpen(!sheetOpen)}
            style={{
              padding:'10px 16px 8px',
              borderBottom:'1px solid #f0eeea',
              cursor:'pointer',
              flexShrink: 0,
            }}
          >
            <div style={{
              width:36, height:4, background:'#d3d1c7',
              borderRadius:2, margin:'0 auto 10px',
            }} />
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <div>
                <div style={{fontSize:14,fontWeight:500}}>
                  {parcel ? parcel.address : 'LandFrame'}
                </div>
                <div style={{fontSize:11,color:'#888'}}>
                  {parcel ? `${parcel.city}, ${parcel.state}` : 'Tap the map or search to load a parcel'}
                </div>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:12}}>
                {parcel && (
                  <div style={{
                    background:'#E1F5EE', color:'#0D9488',
                    fontWeight:600, fontSize:14,
                    padding:'3px 10px', borderRadius:6,
                  }}>
                    {parcel.zoning_code}
                  </div>
                )}
                <span style={{fontSize:11,color:'#0D9488'}}>
                  {sheetOpen ? '▼' : '▲'}
                </span>
              </div>
            </div>
            {/* Sign out row */}
            <div style={{display:'flex', justifyContent:'space-between', marginTop:8}}>
              <span style={{fontSize:10,color:'#aaa'}}>{session.user.email}</span>
              <span style={{fontSize:10,color:'#0D9488',cursor:'pointer'}}
                onClick={(e) => { e.stopPropagation(); supabase.auth.signOut(); }}>
                Sign out
              </span>
            </div>
          </div>

          {/* Scrollable content */}
          <div style={{overflowY:'auto', flex:1}}>
            {sidebarContent}
          </div>
        </div>

      </div>
    </>
  );
}

const Section = ({ label }: { label: string }) => (
  <div style={{
    fontSize:10, fontWeight:500, color:'#888',
    textTransform:'uppercase', letterSpacing:'0.06em',
    marginTop:14, marginBottom:6
  }}>{label}</div>
);

const Row = ({ label, value }: { label: string; value: any }) => (
  <div style={{
    display:'flex', justifyContent:'space-between',
    padding:'5px 0', borderBottom:'0.5px solid #f0eeea', fontSize:12
  }}>
    <span style={{color:'#888',textTransform:'capitalize',flexShrink:0,paddingRight:8}}>
      {label}
    </span>
    <span style={{fontWeight:500,textAlign:'right',color:'#1a1a18'}}>
      {value || '—'}
    </span>
  </div>
);
